// shared-lib/cabinet-runs/upperStack.js
//
// A STACKED row of uppers (plan 2026-10-21, Phase 3, C17–C21): a second row
// of wall cabinets standing on the uppers' top line, following their widths,
// asked for by `run.upperSpec.stack` and placed by the band
// (`upperBand.upperBandFor` → `band.stack`).
//
// What stands where:
//  - over every BUILT upper: a filler stays a filler, an open shelf gets
//    doors, a bay the user cleared ("No cabinet") has nothing to stand on,
//    and a bay whose record says `noStack: true` opts out (C18);
//  - at a height the family OFFERS (user ruling 2026-10-21: "it should only
//    allow products available in the product family",
//    `upperHeights.offeredUpperHeightIn`): the stock heights with no size
//    catalog, the line's own at each width with one — the tallest that fits;
//    a width it makes none of split into equal widths it does (the window
//    rule, `upperTier.buildUpperBoxOrSplit`), a width with nothing left bare
//    with a warning (C17, C18), and a filler as tall as the box beside it;
//  - over the corner L as an L where the line makes a corner wall unit that
//    tall; where it makes none (Venus's pies are 30"+), as two straight
//    boxes covering the L's legs — one running into the corner, the other
//    butting into its face; over a blind corner, a plain box as wide as the
//    blind unit (the user, 2026-11-01, replacing C20's open corner);
//  - over a valance bay: the row runs on over the sink (the user,
//    2026-11-01);
//  - with glass doors where the stack asks for them (`stack.glass`) and the
//    line makes a glass door for the box — a glass row packed in the widths
//    its line glazes, the uppers' own C13 rule (`glazableFillOpts`).
//
// A stack box is an ordinary upper the plain fill made (C21): its own id
// stamp (`upper_cabinet_stack_…`, `kitchenUpperCabinet_stack_…`, so it can
// never share an id with the box under it), `category.stack`, `stackOf`
// naming that box, the ordinary label (a price book prices it by size), and
// no override of its own — it wears the look of the upper under it
// (`fillRun`'s appearance pass). Pure.

const { familyOf, familyFillOpts } = require('./families');
const { buildUpperBoxOrSplit, splitWideBox } = require('./upperTier');
const { buildCornerUppers, eachCabinetCorner } = require('./corners');
const { effectiveAppearance } = require('./bayOverrides');
const {
  glazableFillOpts,
  glassUnavailableReason,
  isSilentGlassRefusal,
  solidUppersWarning,
  vendorOf,
} = require('./glassUppers');
const { statedStack, stackUnavailableReason } = require('./upperBand');
const { worldToWallParam } = require('../kitchen-assembly/wallMath');
const {
  INCHES_TO_METERS,
  UPPER_CABINET_DIMENSIONS,
} = require('../kitchen-assembly/constants');
const { isRack } = require('./racks');

// stands on the uppers' line, at a height the family offers, its own id stamp
const STACK_BUILD = {
  sitOnBottom: true,
  offeredHeights: true,
  stampTag: 'stack',
};
/** Two stacked boxes touching along their wall: float, not a reveal. */
const ABUT_M = 0.002;

/** The span a built cabinet occupies along its wall (metres). */
function spanOf(wall, cabinet) {
  const mid = worldToWallParam(wall, cabinet.center);
  const half = cabinet.dimensions.x / 2;
  return { start: mid - half, end: mid + half };
}

/**
 * Does anything stand over this built upper? Over a blind corner's unit and
 * a valance bay too (the user, 2026-11-01: "the stacked uppers should be
 * allowed to go on top of the corner uppers as well as over the valences").
 */
const takesStack = (ub) =>
  ub.override?.noStack !== true &&
  // an open rack is no box to stand on (`racks.js`), nor an angled end's
  // three-sided top (`angledEnds.js`)
  !isRack(ub.cabinet) &&
  !ub.cabinet?.category?.angledEnd;

/** The band the stacked row hangs in, as the box builders read one. */
const stackBandOf = (stack) => ({
  bottomY: stack.bottomY,
  topY: stack.topY,
  height: stack.topY - stack.bottomY,
});

/**
 * A stack the run SAYS it wants that the band has no room for — its uppers
 * made taller, or hung from the ceiling, after it was set (review finding
 * 2026-10-21). It stays saved, and the run says why it builds nothing.
 */
function noRoomWarnings(run, ctx) {
  const stated = statedStack(run.upperSpec?.stack);
  if (!stated || !ctx.upperBand) return [];
  const reason = stackUnavailableReason(ctx.upperBand, stated.height);
  if (!reason) return [];
  return [
    {
      severity: 'warning',
      type: 'upper-stack',
      message: `No stacked row — ${reason[0].toLowerCase()}${reason.slice(1)}`,
    },
  ];
}

/** C18: one warning per width the line makes nothing above the uppers for. */
function bareWidthWarnings(run, bareWidthsIn) {
  const counts = new Map();
  for (const w of bareWidthsIn) counts.set(w, (counts.get(w) || 0) + 1);
  const vendor = vendorOf(familyOf(run));
  return [...counts].map(([w, n]) => ({
    severity: 'warning',
    type: 'upper-stack',
    message: `No stacked cabinet over ${n === 1 ? '1 upper' : `${n} uppers`}: ${vendor} makes no ${+w.toFixed(2)}"-wide wall cabinet that fits above the uppers`,
  }));
}

/**
 * The stacked boxes over one built upper — split first into widths the line
 * glazes when the row is glass.
 */
function stackOverUpper(run, ctx, ub, fillOpts) {
  const { wall } = run.segments[ub.segIndex];
  const planned = {
    ...spanOf(wall, ub.cabinet),
    ...(ub.cabinet.category?.filler ? { filler: true } : {}),
  };
  const pieces =
    ctx.upperBand.stack.glass && !planned.filler
      ? splitWideBox(planned, fillOpts)
      : [planned];
  return pieces.flatMap((piece) =>
    buildUpperBoxOrSplit(
      wall,
      piece,
      ctx.floorY,
      stackBandOf(ctx.upperBand.stack),
      [],
      familyOf(run),
      fillOpts,
      STACK_BUILD
    ).map(({ cabinet }) => cabinet)
  );
}

/**
 * The stacked filler over a filler upper: a filler is ripped to fit, not a
 * height the family offers, so it stands as tall as the tallest stacked box
 * it touches — a filler the full gap would stand proud of the boxes beside it
 * under a line whose heights differ by width. Touching none, there is no row
 * for it to keep continuous and it is not built (as `dropLoneFillerUppers`).
 */
function stackOverFiller(run, ctx, ub, fillOpts, stacked) {
  const { wall } = run.segments[ub.segIndex];
  const span = spanOf(wall, ub.cabinet);
  const touching = stacked.filter((c) => {
    if ((c.parentWallIds || [])[0] !== wall.id) return false;
    const o = spanOf(wall, c);
    return (
      Math.abs(o.end - span.start) <= ABUT_M ||
      Math.abs(o.start - span.end) <= ABUT_M
    );
  });
  if (!touching.length) return [];
  const height = Math.max(...touching.map((c) => c.dimensions.y));
  const { bottomY } = ctx.upperBand.stack;
  return buildUpperBoxOrSplit(
    wall,
    { ...span, filler: true },
    ctx.floorY,
    { bottomY, topY: bottomY + height, height },
    [],
    familyOf(run),
    fillOpts,
    STACK_BUILD
  ).map(({ cabinet }) => cabinet);
}

/**
 * Two straight stacked boxes over corner `cornerIndex`'s L, where the line
 * makes no corner unit that short: one on the first leg from the corner out
 * to the L's reach, the other on the second leg from that box's face out to
 * its reach (24" x 24" over a Venus pie: a W24 and a W12), each a box the
 * line makes, split as any stacked box is.
 */
function straightBoxesOverCorner(run, ctx, cornerIndex, reach, fillOpts) {
  const band = stackBandOf(ctx.upperBand.stack);
  const family = familyOf(run);
  const depth = UPPER_CABINET_DIMENSIONS.depth;
  const out = [];
  eachCabinetCorner(run, new Set([cornerIndex]), ({ corner, legs }) => {
    const paramAt = (leg, d) =>
      worldToWallParam(leg.wall, {
        x: corner.x + leg.dir.x * d,
        z: corner.z + leg.dir.z * d,
      });
    const spanOn = (leg, from, to) => {
      const a = paramAt(leg, from);
      const b = paramAt(leg, to);
      return { start: Math.min(a, b), end: Math.max(a, b) };
    };
    const [legA, legB] = legs;
    const pieces = [
      { wall: legA.wall, span: spanOn(legA, 0, reach.reachA) },
      { wall: legB.wall, span: spanOn(legB, depth, reach.reachB) },
    ];
    for (const { wall, span } of pieces)
      if (span.end - span.start > ABUT_M)
        out.push(
          ...buildUpperBoxOrSplit(
            wall,
            span,
            ctx.floorY,
            band,
            [],
            family,
            fillOpts,
            STACK_BUILD
          ).map(({ cabinet }) => cabinet)
        );
  });
  return out;
}

/**
 * The stacked boxes over each corner L that was built: an L where the line
 * makes one that short, else two straight boxes (`straightBoxesOverCorner`).
 */
function stackOverCorners(run, ctx, lCorners, cornerUppers, fillOpts) {
  const under = new Map(cornerUppers.map((c) => [c.cornerIndex, c]));
  const legs = new Map([...lCorners].filter(([i]) => under.has(i)));
  if (!legs.size) return [];
  const asL = buildCornerUppers(
    run,
    ctx.floorY,
    legs,
    stackBandOf(ctx.upperBand.stack),
    { stack: true }
  );
  const stackedL = new Set(asL.map((c) => c.cornerIndex));
  const out = asL.map((cabinet) => {
    const below = under.get(cabinet.cornerIndex);
    // the corner tools find a corner's L by its index: only the L the
    // corner plan decided carries it
    delete cabinet.cornerIndex;
    return { cabinet, under: below, record: null };
  });
  for (const [i, reach] of legs) {
    if (stackedL.has(i)) continue;
    for (const cabinet of straightBoxesOverCorner(run, ctx, i, reach, fillOpts))
      out.push({ cabinet, under: under.get(i), record: null });
  }
  return out;
}

/**
 * The stacked row over a run's built uppers.
 *
 * @param {Object} run
 * @param {Object} ctx `fillRun`'s context — reads `upperBand.stack`, `floorY`
 * @param {{ built: Object[], lCorners: Map, cornerUppers: Object[] }} uppers
 *   `built` — the straight uppers as built (`fillUppers`' `upperBays`, after
 *   the lone fillers are dropped); `lCorners` and `cornerUppers` — the corner
 *   legs and the corner L's that were actually built
 * @returns {{ stack: { cabinet: Object, under: Object, record: Object|null,
 *   glass: boolean }[], warnings: Object[] }} `under` — the box it stands on;
 *   `record` — that box's record, whose look the stack box wears
 */
function fillStackRow(
  run,
  ctx,
  { built = [], lCorners = new Map(), cornerUppers = [] }
) {
  const stack = ctx.upperBand?.stack;
  if (!stack) return { stack: [], warnings: noRoomWarnings(run, ctx) };
  const family = familyOf(run);
  const fillOpts = stack.glass
    ? glazableFillOpts(family, familyFillOpts(run))
    : familyFillOpts(run);
  const out = [];
  const bare = [];
  const fillers = [];
  for (const ub of built) {
    if (!takesStack(ub)) continue;
    // a filler takes its height from the stacked boxes beside it, below
    if (ub.cabinet.category?.filler) {
      fillers.push(ub);
      continue;
    }
    const boxes = stackOverUpper(run, ctx, ub, fillOpts);
    if (!boxes.length) {
      bare.push(ub.cabinet.dimensions.x / INCHES_TO_METERS);
      continue;
    }
    for (const cabinet of boxes)
      out.push({ cabinet, under: ub.cabinet, record: ub.override || null });
  }
  const stackedBoxes = out.map((s) => s.cabinet);
  for (const ub of fillers) {
    for (const cabinet of stackOverFiller(run, ctx, ub, fillOpts, stackedBoxes))
      out.push({ cabinet, under: ub.cabinet, record: ub.override || null });
  }
  out.push(...stackOverCorners(run, ctx, lCorners, cornerUppers, fillOpts));

  const solid = [];
  for (const s of out) {
    s.cabinet.category = { ...s.cabinet.category, stack: true };
    s.cabinet.stackOf = s.under.id;
    s.glass = false;
    if (!stack.glass || s.cabinet.category.filler) continue;
    const look = effectiveAppearance({ override: s.record, run, ctx }) || {};
    const reason = glassUnavailableReason(run, s.cabinet, look);
    if (!reason) s.glass = true;
    else if (!isSilentGlassRefusal(s.cabinet, reason)) solid.push(reason);
  }
  const warnings = bareWidthWarnings(run, bare);
  const solidWarning = solidUppersWarning(run, solid, [
    'stacked cabinet',
    'stacked cabinets',
  ]);
  if (solidWarning) warnings.push(solidWarning);
  return { stack: out, warnings };
}

module.exports = { fillStackRow };
